import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { stockWriteOffsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";


/**
 * UC-M1-INV-05 — Write-off detail. Draft shows a Post button (writes one negative
 * ledger row per line at batch cost). Posted documents are read-only — corrections
 * require a positive stock adjustment.
 */
export function StockWriteOffDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canPost = useAuth(s => s.hasPermission("inventory.writeoff.post"));
  const [postOpen, setPostOpen] = useState(false);

  const query = useQuery({
    queryKey: ["stock-writeoff", id],
    queryFn: () => stockWriteOffsApi.get(id),
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => stockWriteOffsApi.post(id, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-writeoff", id] });
      qc.invalidateQueries({ queryKey: ["stock-writeoffs"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const wo = query.data;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{wo.documentNo}</Typography>
          <StatusPill label={wo.status} />
        </Stack>
        {wo.status === "Draft" && canPost && (
          <Button variant="contained" color="success" startIcon={<PostAddIcon />}
            onClick={() => setPostOpen(true)}>Post write-off</Button>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{formatDate(wo.documentDate)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Warehouse</Typography>
            <Typography>{wo.warehouseName}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Header reason</Typography>
            <Typography>{wo.reason ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Total qty</Typography>
            <Typography>{wo.totalQty}</Typography></Stack>
        </Stack>
        {wo.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{wo.notes}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Expiry</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {wo.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell>{l.batchNo ?? "—"}</TableCell>
                <TableCell>{l.expiryDate ? formatDate(l.expiryDate) : "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "error.main" }}>−{l.qty}</TableCell>
                <TableCell><Chip size="small" variant="outlined" color="error" label={l.reasonCode} /></TableCell>
                <TableCell>{l.lineNotes ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/inventory/writeoffs")}>Back to list</Button>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPostOpen(false)}>Post write-off {wo.documentNo}?</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting writes one immutable WRITE_OFF ledger row per line, valued at the
            batch's current cost price. The ledger row reduces on-hand stock — corrections
            require a new positive stock adjustment.
          </DialogContentText>
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={postMutation.isPending}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}>Post</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
